import { useState } from "react";
import { format } from "date-fns";
import { API, useAuth } from "../App";
import axios from "axios";
import { toast } from "sonner";

export default function ReceiptPreview({ sale, onSaleUpdated }) {
  const { getAuthHeader } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [currentSale, setCurrentSale] = useState(sale);

  if (!currentSale) return null;

  const saleDate = new Date(currentSale.created_at);
  const receiptNum = currentSale.receipt_number;
  const barcodeNum = receiptNum.replace(/[^0-9]/g, '').slice(-8) || '00000001';

  const startEditing = () => {
    setEditDate(format(saleDate, "yyyy-MM-dd"));
    setEditTime(format(saleDate, "HH:mm"));
    setIsEditing(true);
  };

  const saveEdit = async () => {
    try {
      const newDateTime = new Date(`${editDate}T${editTime}:00`);
      const isoDate = newDateTime.toISOString();
      const response = await axios.put(
        `${API}/sales/${currentSale.id}/edit`,
        { created_at: isoDate },
        getAuthHeader()
      );
      setCurrentSale(response.data);
      setIsEditing(false);
      toast.success("Receipt date updated");
      if (onSaleUpdated) onSaleUpdated(response.data);
    } catch (error) {
      toast.error("Failed to update receipt");
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
  };

  const handlePrint = () => {
    const printSaleDate = new Date(currentSale.created_at);
    const printWindow = window.open('', '', 'width=320,height=500');
    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt</title>
          <link href="https://fonts.googleapis.com/css2?family=Libre+Barcode+39&display=swap" rel="stylesheet">
          <style>
            @page { size: 72mm auto; margin: 0; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', Arial, sans-serif; 
              font-size: 11px; 
              width: 72mm; 
              padding: 3mm;
              line-height: 1.35;
              color: #000;
            }
            .header { text-align: center; padding-bottom: 6px; border-bottom: 1px solid #000; }
            .store-name { font-size: 14px; font-weight: 700; margin-bottom: 2px; }
            .store-sub { font-size: 11px; font-weight: 600; }
            .store-info { font-size: 10px; color: #333; }
            .section { padding: 5px 0; border-bottom: 1px dashed #999; }
            .row { display: flex; justify-content: space-between; margin: 2px 0; font-size: 10px; }
            .row-bold { font-weight: 700; }
            .items-header { display: flex; font-weight: 700; font-size: 9px; padding: 3px 0; border-bottom: 1px solid #ccc; margin-bottom: 3px; }
            .item { display: flex; font-size: 10px; margin: 2px 0; }
            .col-qty { width: 28px; }
            .col-name { flex: 1; }
            .col-price { width: 42px; text-align: right; }
            .col-total { width: 48px; text-align: right; font-weight: 600; }
            .total-section { padding: 5px 0; }
            .grand-total { display: flex; justify-content: space-between; font-size: 13px; font-weight: 700; margin-top: 3px; padding-top: 4px; border-top: 1px solid #000; }
            .payment { padding: 5px 0; border-bottom: 1px dashed #999; }
            .footer { text-align: center; padding-top: 5px; font-size: 9px; }
            .barcode-container { text-align: center; margin-top: 6px; padding: 4px 0; }
            .barcode { font-family: 'Libre Barcode 39', cursive; font-size: 32px; line-height: 1; }
            .barcode-num { font-family: 'Segoe UI', Arial, sans-serif; font-size: 9px; font-weight: 600; margin-top: 2px; letter-spacing: 1px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="store-name">NIMSI'S STATIONERY</div>
            <div class="store-sub">& HOME SUPPLY</div>
            <div class="store-info">San Narciso, Belize • Tel: 624-3800</div>
          </div>
          
          <div class="section">
            <div class="row"><span>Receipt:</span><span class="row-bold">${receiptNum}</span></div>
            <div class="row"><span>Date:</span><span>${format(printSaleDate, "MM/dd/yyyy hh:mm a")}</span></div>
            <div class="row"><span>Cashier:</span><span>${currentSale.username}</span></div>
          </div>
          
          ${currentSale.is_return ? '<div style="text-align:center;padding:4px;background:#eee;font-weight:700;font-size:11px;margin:4px 0;">*** REFUND ***</div>' : ''}
          
          <div class="section">
            <div class="items-header">
              <span class="col-qty">Qty</span>
              <span class="col-name">Item</span>
              <span class="col-price">Price</span>
              <span class="col-total">Total</span>
            </div>
            ${currentSale.items.map(item => `
              <div class="item">
                <span class="col-qty">${item.quantity}</span>
                <span class="col-name">${item.name}</span>
                <span class="col-price">$${item.price.toFixed(2)}</span>
                <span class="col-total">$${(item.quantity * item.price).toFixed(2)}</span>
              </div>
            `).join('')}
          </div>
          
          <div class="total-section">
            <div class="row"><span>Subtotal:</span><span>$${Math.abs(currentSale.subtotal).toFixed(2)}</span></div>
            <div class="row"><span>Tax (0%):</span><span>$0.00</span></div>
            <div class="grand-total"><span>TOTAL:</span><span>$${Math.abs(currentSale.total).toFixed(2)}</span></div>
          </div>
          
          <div class="payment">
            <div class="row row-bold"><span>${currentSale.payment_method}:</span><span>$${currentSale.amount_paid.toFixed(2)}</span></div>
            ${currentSale.change_due > 0 ? `<div class="row row-bold"><span>Change:</span><span>$${currentSale.change_due.toFixed(2)}</span></div>` : ''}
          </div>
          
          <div class="footer">
            <div>Returns within 7 days with receipt</div>
            <div style="font-weight:700;font-size:10px;margin-top:3px;">Thank you for shopping!</div>
          </div>
          
          <div class="barcode-container">
            <div class="barcode">*${barcodeNum}*</div>
            <div class="barcode-num">${barcodeNum}</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
  };
  
  return (
    <div data-testid="receipt-preview">
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Libre+Barcode+39&display=swap');`}
      </style>
      <div style={{ 
        width: '280px', 
        padding: '12px 14px', 
        fontFamily: "'Segoe UI', Arial, sans-serif", 
        fontSize: '11px',
        backgroundColor: '#fff',
        border: '1px solid #ddd',
        borderRadius: '4px',
        color: '#000',
        lineHeight: '1.4'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', paddingBottom: '8px', borderBottom: '1px solid #000' }}>
          <div style={{ fontSize: '15px', fontWeight: '700' }}>NIMSI'S STATIONERY</div>
          <div style={{ fontSize: '12px', fontWeight: '600' }}>& HOME SUPPLY</div>
          <div style={{ fontSize: '10px', color: '#333', marginTop: '2px' }}>San Narciso, Belize • Tel: 624-3800</div>
        </div>

        {/* Receipt Info */}
        <div style={{ padding: '6px 0', borderBottom: '1px dashed #999', fontSize: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
            <span>Receipt:</span>
            <span style={{ fontWeight: '700' }}>{receiptNum}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', alignItems: 'center' }}>
            <span>Date:</span>
            {isEditing ? (
              <div style={{ display: 'flex', gap: '4px' }}>
                <input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  style={{ fontSize: '10px', padding: '1px 3px', border: '1px solid #999', borderRadius: '2px', width: '110px' }}
                  data-testid="edit-receipt-date"
                />
                <input
                  type="time"
                  value={editTime}
                  onChange={(e) => setEditTime(e.target.value)}
                  style={{ fontSize: '10px', padding: '1px 3px', border: '1px solid #999', borderRadius: '2px', width: '70px' }}
                  data-testid="edit-receipt-time"
                />
              </div>
            ) : (
              <span>{format(saleDate, "MM/dd/yyyy hh:mm a")}</span>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Cashier:</span>
            <span>{currentSale.username}</span>
          </div>
        </div>
        
        {currentSale.is_return && (
          <div style={{ textAlign: 'center', padding: '4px', background: '#eee', fontWeight: '700', fontSize: '11px', margin: '4px 0' }}>
            *** REFUND ***
          </div>
        )}

        {/* Items */}
        <div style={{ padding: '6px 0', borderBottom: '1px dashed #999' }}>
          <div style={{ display: 'flex', fontWeight: '700', fontSize: '9px', paddingBottom: '3px', borderBottom: '1px solid #ccc', marginBottom: '4px' }}>
            <span style={{ width: '28px' }}>Qty</span>
            <span style={{ flex: 1 }}>Item</span>
            <span style={{ width: '40px', textAlign: 'right' }}>Price</span>
            <span style={{ width: '46px', textAlign: 'right' }}>Total</span>
          </div>
          {currentSale.items.map((item, index) => (
            <div key={index} style={{ display: 'flex', fontSize: '10px', marginBottom: '2px' }}>
              <span style={{ width: '28px' }}>{item.quantity}</span>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
              <span style={{ width: '40px', textAlign: 'right' }}>${item.price.toFixed(2)}</span>
              <span style={{ width: '46px', textAlign: 'right', fontWeight: '600' }}>${(item.quantity * item.price).toFixed(2)}</span>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div style={{ padding: '6px 0', fontSize: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
            <span>Subtotal:</span>
            <span>${Math.abs(currentSale.subtotal).toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
            <span>Tax (0%):</span>
            <span>$0.00</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: '700', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #000' }}>
            <span>TOTAL:</span>
            <span>${Math.abs(currentSale.total).toFixed(2)}</span>
          </div>
        </div>

        {/* Payment */}
        <div style={{ padding: '5px 0', borderBottom: '1px dashed #999', fontSize: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700' }}>
            <span>{currentSale.payment_method}:</span>
            <span>${currentSale.amount_paid.toFixed(2)}</span>
          </div>
          {currentSale.change_due > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700' }}>
              <span>Change:</span>
              <span>${currentSale.change_due.toFixed(2)}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', paddingTop: '6px', fontSize: '9px' }}>
          <div>Returns within 7 days with receipt</div>
          <div style={{ fontWeight: '700', fontSize: '10px', marginTop: '3px' }}>Thank you for shopping!</div>
        </div>

        {/* Scannable Barcode - Code 39 format */}
        <div style={{ textAlign: 'center', marginTop: '8px', padding: '4px 0' }}>
          <div style={{ 
            fontFamily: "'Libre Barcode 39', cursive", 
            fontSize: '36px', 
            lineHeight: '1',
            letterSpacing: '0'
          }}>
            *{barcodeNum}*
          </div>
          <div style={{ fontSize: '9px', fontWeight: '600', marginTop: '2px', letterSpacing: '1px' }}>
            {barcodeNum}
          </div>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
        {isEditing ? (
          <>
            <button 
              onClick={saveEdit}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: '#16a34a',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              data-testid="save-edit-btn"
            >
              SAVE CHANGES
            </button>
            <button 
              onClick={cancelEdit}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              data-testid="cancel-edit-btn"
            >
              CANCEL
            </button>
          </>
        ) : (
          <>
            <button 
              onClick={startEditing}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              data-testid="edit-receipt-btn"
            >
              EDIT DATE
            </button>
            <button 
              onClick={handlePrint}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: '#1a1a1a',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              data-testid="print-now-btn"
            >
              PRINT RECEIPT
            </button>
          </>
        )}
      </div>
    </div>
  );
}
